import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, defer, finalize, map, of, tap } from 'rxjs';
import { log } from '../utils/logger';

/**
 * Agency-defined disposition, mirroring the persisted CustomDispoResult.
 * `mappedStatus` is a Consumer.Dialer.Result and `category` a
 * Consumer.Dialer.ResultCategory, both in their numeric proto form.
 */
export interface CustomDispositionResult {
  id: string;
  name: string;
  icon?: string;
  mappedStatus: number;
  category: number;
}

/** One disposition an outcome code can be mapped onto. */
export interface DispositionOption {
  /** Result name for built-ins, CustomDispoResult id for agency dispositions. */
  id: string;
  custom: boolean;
  /** Serialized Consumer.Dialer.Result. */
  result: string;
  /** Serialized Consumer.Dialer.ResultCategory. */
  resultCategory: string;
  /** Only set when `custom` is true. */
  customResult?: CustomDispositionResult;
}

const logger = log.getLogger('disposition.service');

/**
 * Loads the dispositions an agency can map task outcomes onto.
 *
 * NOTE: This service requires the API base URL to be provided via setApiUrl.
 */
@Injectable({ providedIn: 'root' })
export class DispositionService {
  private readonly http = inject(HttpClient);

  // API URL will be set by the consuming app
  private apiUrl = '';

  private loadedAgencyId?: string;

  /**
   * The agency whose request owns the shared signal, used to discard late
   * responses. Released when a request is cancelled before it answers.
   */
  private requestedAgencyId?: string;

  /**
   * Set the API base URL (call this before the first load)
   */
  setApiUrl(baseUrl: string): void {
    this.apiUrl = `${baseUrl}/v1/dialer/dispositions`;
  }

  dispositions = signal<DispositionOption[]>([]);
  loading = signal(false);

  /**
   * Loads the agency's dispositions. The list is small and rarely changes, so
   * it is fetched once per agency and served from the signal after that.
   */
  load(agencyId: string): Observable<DispositionOption[]> {
    if (!agencyId || !this.apiUrl) {
      return of([]);
    }
    // Deferred so the cache check and the signal writes below happen on
    // subscribe rather than on call, leaving nothing to clean up if the
    // returned Observable is never subscribed to.
    return defer(() => {
      if (this.loadedAgencyId === agencyId) {
        return of(this.dispositions());
      }

      // Claim the request before it goes out, and drop the previous agency's
      // list so a switch never shows another agency's dispositions — not while
      // the request is in flight, and not if it fails.
      this.requestedAgencyId = agencyId;
      this.loadedAgencyId = undefined;
      this.dispositions.set([]);
      this.loading.set(true);

      let settled = false;

      return this.http
        .get<{ dispositions: DispositionOption[] }>(this.apiUrl, {
          params: { agencyId },
        })
        .pipe(
          map(response => response.dispositions ?? []),
          tap(dispositions => {
            settled = true;
            // Publish when this request still holds the claim, or when no one
            // does because a newer request was cancelled before responding.
            if (
              this.requestedAgencyId !== undefined &&
              this.requestedAgencyId !== agencyId
            ) {
              return;
            }
            this.dispositions.set(dispositions);
            this.loadedAgencyId = agencyId;
            this.requestedAgencyId = agencyId;
          }),
          catchError(error => {
            logger.error('Error loading dispositions', { agencyId, error });
            // A failure is still an answer for this agency, so the claim is
            // kept and an older response cannot overwrite the empty list.
            settled = true;
            return of([]);
          }),
          // finalize also runs when the caller unsubscribes, which cancels the
          // request without emitting, so loading cannot be left stuck on.
          finalize(() => {
            if (this.requestedAgencyId !== agencyId) {
              return;
            }
            if (!settled) {
              this.requestedAgencyId = undefined;
            }
            this.loading.set(false);
          })
        );
    });
  }
}
